import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { HubGate, HubMessage } from "@/pages/hub/Hub";

const CALLOUT_PREFIXES = ["WHY:", "NOTE:", "EXAMPLE:", "SCRIPT:"];

function Text({ children }) {
  return (
    <>
      {String(children).split("\n").map((line, i) => (
        <span key={i}>
          {i > 0 && <br />}
          {line}
        </span>
      ))}
    </>
  );
}

function Block({ block, checklistOffset, checked, onToggle }) {
  const [kind, data] = block;
  if (kind === "h2")
    return <h2 className="font-display mt-10 mb-3 text-2xl font-bold uppercase tracking-tight text-slate-100">{data}</h2>;
  if (kind === "h3")
    return <h3 className="font-mono-ind mt-7 mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FF5500]">{data}</h3>;
  if (kind === "p")
    return <p className="mb-4 text-[15px] leading-relaxed text-slate-300"><Text>{data}</Text></p>;
  if (kind === "bullets")
    return (
      <ul className="mb-5 space-y-2.5">
        {data.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-300">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#FF5500]" />
            <Text>{item}</Text>
          </li>
        ))}
      </ul>
    );
  if (kind === "checklist")
    return (
      <div className="mb-5 space-y-2">
        {data.map((item, i) => {
          const globalIdx = checklistOffset + i;
          const isChecked = checked.has(globalIdx);
          return (
            <button
              key={i}
              data-testid={`checklist-item-${globalIdx}`}
              onClick={() => onToggle(globalIdx)}
              className={`flex w-full items-start gap-3 border px-4 py-3 text-left text-[15px] transition-colors ${
                isChecked
                  ? "border-[#FF5500]/50 bg-[#FF5500]/10 text-slate-400"
                  : "border-white/10 bg-[#121418] text-slate-300 hover:border-[#FF5500]/40"
              }`}
              aria-pressed={isChecked}
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border-2 border-[#FF5500] ${
                  isChecked ? "bg-[#FF5500]" : ""
                }`}
              >
                {isChecked && (
                  <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 text-white" fill="none">
                    <path d="M1.5 5.5L4 8L8.5 2" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                )}
              </span>
              <span className={isChecked ? "line-through decoration-slate-500" : ""}>
                <Text>{item}</Text>
              </span>
            </button>
          );
        })}
      </div>
    );
  if (kind === "table")
    return (
      <div className="mb-6 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {data.headers.map((h, i) => (
                <th key={i} className="border-b-2 border-[#FF5500] bg-[#121418] px-4 py-3 text-left font-mono-ind text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-200">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i} className="border-b border-white/10">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-3 align-top leading-relaxed text-slate-300">
                    <Text>{cell}</Text>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (kind === "callout") {
    let label = "Important";
    let text = data;
    for (const prefix of CALLOUT_PREFIXES) {
      if (typeof data === "string" && data.startsWith(prefix)) {
        label = prefix.slice(0, -1);
        text = data.slice(prefix.length).trim();
        break;
      }
    }
    return (
      <div className="mb-5 border-l-2 border-[#FF5500] bg-[#FF5500]/8 px-5 py-4">
        <span className="font-mono-ind mr-3 text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5500]">
          {label}
        </span>
        <span className="text-[15px] leading-relaxed text-slate-200"><Text>{text}</Text></span>
      </div>
    );
  }
  return null;
}

export default function HubReader() {
  const { sectionSlug, resourceId } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    setDoc(null);
    setError("");
    setChecked(new Set());
    api
      .get(`/hub/resource/${resourceId}`)
      .then((r) => {
        setDoc(r.data);
        api.post("/hub/progress", { resource_id: resourceId }).catch(() => {});
        api
          .get(`/hub/checklist/${resourceId}`)
          .then((res) => setChecked(new Set(res.data.checked || [])))
          .catch(() => {});
      })
      .catch((e) => setError(e?.response?.data?.detail || "error"));
  }, [user, resourceId]);

  useEffect(() => {
    if (doc) document.title = `${doc.title} — Sitebound Job Seeker Hub`;
  }, [doc]);

  const toggle = (idx) => {
    const next = new Set(checked);
    const nowChecked = !next.has(idx);
    if (nowChecked) next.add(idx);
    else next.delete(idx);
    setChecked(next);
    api
      .post("/hub/checklist", { resource_id: resourceId, item: idx, checked: nowChecked })
      .catch(() => {});
  };

  if (user === false) {
    return (
      <HubMessage title="Sign in to your hub" body="Sign in with the email you purchased with."
        linkTo="/account" linkText="Sign in" testId="reader-gate-signin" />
    );
  }
  if (error) return <HubGate error={error} />;
  if (!doc) {
    return <div className="mx-auto max-w-3xl px-4 py-24 text-sm uppercase tracking-widest text-slate-500">Loading…</div>;
  }

  const totalTasks = doc.blocks.reduce(
    (n, [kind, data]) => n + (kind === "checklist" ? data.length : 0), 0
  );
  let runningOffset = 0;

  return (
    <div data-testid={`hub-reader-${doc.id}`} className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center gap-4">
        <Link
          data-testid="reader-back-section"
          to={`/hub/${doc.section.slug}`}
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400 transition-colors hover:text-[#FF5500]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {doc.section.number} {doc.section.name}
        </Link>
        <Link
          data-testid="reader-back-hub"
          to="/hub"
          className="text-xs font-semibold uppercase tracking-widest text-slate-500 transition-colors hover:text-[#FF5500]"
        >
          Hub
        </Link>
      </div>

      <div className="mono-label mt-8">
        Section {doc.section.number} — {doc.section.name}
      </div>
      <h1 className="font-display mt-3 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-100 sm:text-5xl">
        {doc.title}
      </h1>
      <div className="mt-3 flex flex-wrap items-center gap-5 text-xs text-slate-500">
        <span className="flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-[#FF5500]" /> Marked as read
        </span>
        {totalTasks > 0 && (
          <span data-testid="task-progress" className="font-mono-ind uppercase tracking-[0.18em]">
            {checked.size} of {totalTasks} tasks completed
          </span>
        )}
      </div>
      <div className="mt-2 h-px w-full bg-white/10" />

      <article className="mt-8">
        {doc.blocks.map((block, i) => {
          const offset = runningOffset;
          if (block[0] === "checklist") runningOffset += block[1].length;
          return (
            <Block
              key={i}
              block={block}
              checklistOffset={offset}
              checked={checked}
              onToggle={toggle}
            />
          );
        })}
      </article>

      <div className="mt-12 flex flex-wrap gap-4 border-t border-white/10 pt-8">
        <Link
          data-testid="reader-footer-section"
          to={`/hub/${doc.section.slug}`}
          className="border border-white/25 px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
        >
          Back to section
        </Link>
        <Link
          data-testid="reader-footer-hub"
          to="/hub"
          className="bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
        >
          Job Seeker Hub
        </Link>
      </div>
    </div>
  );
}
